import { ref } from 'vue'

const isLeader = ref(false)
const deviceId = ref(null)
const heartbeatInterval = ref(null)
const checkInterval = ref(null)
const consecutiveFailures = ref(0)
const isSinglePosMode = ref(false)

const HEARTBEAT_INTERVAL_MS = 5000
const CHECK_INTERVAL_MS = 10000
const MAX_CONSECUTIVE_FAILURES = 3

export function usePosLeaderElection() {
  const userStore = useUserStore()
  const logger = useLogger()
  const { getDeviceType } = useMessageOrchestrator()

  async function startLeaderElection() {
    try {
      deviceId.value = await getDeviceType()

      logger.info(`[Leader Election] Starting for device ${deviceId.value}`)

      const response = await useApi(`/api/pos/leader/check-multiple/${userStore.activeBusinessId}`, {}, { method: 'GET' }, 2)
      const hasMultiplePosDevices = response.hasMultiplePosDevices

      if (!hasMultiplePosDevices) {
        logger.info('[Leader Election] Single POS detected - becoming leader without election')
        isSinglePosMode.value = true
        isLeader.value = true
        return
      }

      logger.info('[Leader Election] Multiple POS devices detected - starting election process')
      isSinglePosMode.value = false

      await tryAcquireLeadership()

      if (heartbeatInterval.value) {
        clearInterval(heartbeatInterval.value)
      }
      if (checkInterval.value) {
        clearInterval(checkInterval.value)
      }

      heartbeatInterval.value = setInterval(async () => {
        try {
          if (isLeader.value) {
            await sendHeartbeat()
          }
        } catch (error) {
          logger.error('[Leader Election] Heartbeat interval error:', error)
        }
      }, HEARTBEAT_INTERVAL_MS)

      checkInterval.value = setInterval(async () => {
        try {
          if (!isLeader.value) {
            await tryAcquireLeadership()
          }
        } catch (error) {
          logger.error('[Leader Election] Check interval error:', error)
        }
      }, CHECK_INTERVAL_MS)

      logger.info('[Leader Election] Timers started')
    } catch (error) {
      logger.error('[Leader Election] Failed to start:', error)
      if (heartbeatInterval.value) {
        clearInterval(heartbeatInterval.value)
        heartbeatInterval.value = null
      }
      if (checkInterval.value) {
        clearInterval(checkInterval.value)
        checkInterval.value = null
      }
    }
  }

  async function tryAcquireLeadership() {
    try {
      const response = await useApi(
        '/api/pos/leader/acquire',
        {
          businessId: userStore.activeBusinessId,
          deviceId: deviceId.value,
        },
        { method: 'POST' },
        2
      )

      consecutiveFailures.value = 0

      const wasLeader = isLeader.value
      isLeader.value = response.isLeader

      if (isLeader.value && !wasLeader) {
        logger.info(`[Leader Election] Device ${deviceId.value} is now the leader`)
      } else if (!isLeader.value && wasLeader) {
        logger.warn(`[Leader Election] Device ${deviceId.value} lost leadership`)
      }

      return isLeader.value
    } catch (error) {
      consecutiveFailures.value++
      logger.error(`[Leader Election] Failed to acquire leadership (${consecutiveFailures.value}/${MAX_CONSECUTIVE_FAILURES}):`, error)

      if (consecutiveFailures.value >= MAX_CONSECUTIVE_FAILURES) {
        logger.error('[Leader Election] Max consecutive failures reached - likely network partition')
        isLeader.value = false
      }

      return false
    }
  }

  async function sendHeartbeat() {
    try {
      const response = await useApi(
        '/api/pos/leader/heartbeat',
        {
          businessId: userStore.activeBusinessId,
          deviceId: deviceId.value,
        },
        { method: 'POST' },
        2
      )

      consecutiveFailures.value = 0

      if (!response.success) {
        logger.warn('[Leader Election] Heartbeat failed, trying to reacquire leadership')
        isLeader.value = false
        await tryAcquireLeadership()
      }
    } catch (error) {
      consecutiveFailures.value++
      logger.error(`[Leader Election] Heartbeat error (${consecutiveFailures.value}/${MAX_CONSECUTIVE_FAILURES}):`, error)

      if (consecutiveFailures.value >= MAX_CONSECUTIVE_FAILURES) {
        logger.error('[Leader Election] Max consecutive heartbeat failures - stepping down as leader')
        isLeader.value = false
      } else {
        logger.warn('[Leader Election] Heartbeat failed but within threshold - will retry')
      }
    }
  }

  async function getCurrentLeader() {
    try {
      const response = await useApi(
        `/api/pos/leader/${userStore.activeBusinessId}`,
        {},
        { method: 'GET' },
        2
      )
      return response
    } catch (error) {
      logger.error('[Leader Election] Failed to get current leader:', error)
      return { leaderId: null, isAlive: false }
    }
  }

  async function releaseLeadership() {
    try {
      if (heartbeatInterval.value) {
        clearInterval(heartbeatInterval.value)
        heartbeatInterval.value = null
      }
      if (checkInterval.value) {
        clearInterval(checkInterval.value)
        checkInterval.value = null
      }

      if (isSinglePosMode.value) {
        logger.info('[Leader Election] Single POS mode - no need to release leadership')
        isLeader.value = false
        return
      }

      if (isLeader.value) {
        await useApi(
          '/api/pos/leader/release',
          {
            businessId: userStore.activeBusinessId,
            deviceId: deviceId.value,
          },
          { method: 'POST' },
          2
        )

        logger.info(`[Leader Election] Device ${deviceId.value} released leadership`)
      }

      isLeader.value = false
    } catch (error) {
      logger.error('[Leader Election] Failed to release leadership:', error)
    }
  }

  function stopLeaderElection() {
    releaseLeadership()
  }

  return {
    isLeader: readonly(isLeader),
    deviceId: readonly(deviceId),
    isSinglePosMode: readonly(isSinglePosMode),
    startLeaderElection,
    stopLeaderElection,
    tryAcquireLeadership,
    getCurrentLeader,
    releaseLeadership,
  }
}
